import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios'
import { getToken, clearAuth } from './auth'

class ApiClient {
  private client: AxiosInstance

  constructor() {
    const baseURL = import.meta.env.VITE_OPSERVICE_BASE_URL

    // Warn if using localhost on mobile
    if (baseURL.includes('localhost') && /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)) {
      console.error('[ApiClient] WARNING: Using localhost URL on mobile device. Backend API will not be reachable!')
      console.error('[ApiClient] Please set VITE_OPSERVICE_BASE_URL to a public URL (e.g., ngrok tunnel)')
    }

    this.client = axios.create({
      baseURL,
      headers: {
        'Content-Type': 'application/json',
      },
      timeout: 30000,
    })

    // Request interceptor to add JWT token
    this.client.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        const token = getToken()
        if (token && config.headers) {
          config.headers.Authorization = `Bearer ${token}`
        }
        return config
      },
      (error) => {
        return Promise.reject(error)
      }
    )

    // Response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        if (error.response) {
          // Server responded with error status
          console.error('API Error:', error.response.status, error.response.data)

          // Handle 401 Unauthorized - clear token and potentially re-authenticate
          if (error.response.status === 401) {
            console.warn('[ApiClient] Unauthorized - clearing authentication')
            clearAuth()
            // Could trigger re-authentication here if needed
          }
        } else if (error.request) {
          // Request made but no response
          console.error('Network Error:', error.message)
        } else {
          // Error in request setup
          console.error('Request Error:', error.message)
        }
        return Promise.reject(error)
      }
    )
  }

  getInstance(): AxiosInstance {
    return this.client
  }
}

export const apiClient = new ApiClient().getInstance()
export default apiClient
